import { useEffect, useState } from "react";

interface BotJsLoaderResult {
  loaded: boolean;
  error: Error | null;
}

/**
 * Hook to dynamically load the bot.js script from a configurable URL
 */
export function useBotJsLoader(url: string): BotJsLoaderResult {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Check if script is already loaded
    const existingScript = document.querySelector(
      `script[src="${url}"]`
    ) as HTMLScriptElement;

    if (existingScript) {
      setLoaded(true);
      return () => {};
    }

    // Create and load the script
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.id = "jugl-chatbot-script";

    script.onload = () => {
      setLoaded(true);
      setError(null);
    };

    script.onerror = () => {
      const err = new Error(`Failed to load bot.js from ${url}`);
      setError(err);
      setLoaded(false);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const scriptToRemove = document.getElementById("jugl-chatbot-script");
      if (scriptToRemove) {
        document.body.removeChild(scriptToRemove);
      }
    };
  }, [url]);

  return { loaded, error };
}
